import { __decorate } from '../../../../node_modules/tslib/tslib.es6.mjs.js';
import '../../../../node_modules/@lit/reactive-element/reactive-element.js';
import { html as $ } from '../../../../node_modules/lit-html/lit-html.js';
import { LitElement as s } from '../../../../node_modules/lit-element/lit-element.js';
import { property as e } from '../../../../node_modules/@lit/reactive-element/decorators/property.js';
import { state as t } from '../../../../node_modules/@lit/reactive-element/decorators/state.js';
import '../../../../node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { trackVideoStarted, trackYoutubeVideoStarted, trackYoutubeVideoEnded, trackYoutubeVideoPosition, trackVideoPosition } from './metrics.external-script.js';
import { hasYoutubeConsent, whenYoutubeConsent } from '../video-player/index.js';
import { VideoTypes } from './types.js';
import css_248z from './video-player-plus.scss.js';
import { renderLocalVideo, initializePlyr, handleADStateChange, handleTranscriptionStateChange, renderAudioDescription, renderTranscription } from './utils/localVideoplayer.js';

// eslint-disable-next-line no-use-before-define
const onYtReady = (ctx) => {
    var _a;
    if (ctx.player)
        return;
    // See video-player: the lightbox re-parents .video-container into its
    // overlay on open, so fall back to a document-scope lookup for the mount.
    const playerElement = ((_a = ctx.shadowRoot) === null || _a === void 0 ? void 0 : _a.getElementById(`player_${ctx.videoId}`)) ||
        document.getElementById(`player_${ctx.videoId}`);
    if (!playerElement)
        return;
    const playerVars = {
        rel: '0',
        origin: window.location.origin,
        enablejsapi: '1',
    };
    // See video-player: autoplay is baked in only when the player is created in
    // response to a play gesture, so consent-at-load init never autoplays.
    if (ctx.playWhenReady)
        playerVars.autoplay = 1;
    ctx.player = new window.YT.Player(playerElement, {
        host: 'https://www.youtube-nocookie.com',
        videoId: ctx.videoId,
        playerVars,
        events: {
            onReady: ctx.onYouTubePlayerReady,
            onStateChange: ctx.onYouTubePlayerStateChange,
        },
    });
};
const loadYoutubeIframeAPIScript = () => {
    var _a;
    if (!window.YT && !window.isYTInitializing) {
        window.isYTInitializing = true;
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        (_a = firstScriptTag.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(tag, firstScriptTag);
    }
};
// DCCC-8359: see video-player — the IFrame API script must never load on page
// load without consent; it is requested lazily on consent or user interaction.
// eslint-disable-next-line no-use-before-define
const requestYoutubePlayerInit = (ctx) => {
    var _a;
    if (ctx.player)
        return;
    if ((_a = window.YT) === null || _a === void 0 ? void 0 : _a.Player) {
        onYtReady(ctx);
        return;
    }
    if (!window.videoPlayerReadyCallbacks)
        window.videoPlayerReadyCallbacks = [];
    if (!window.videoPlayerReadyCallbacks.some(cb => cb.context === ctx)) {
        window.videoPlayerReadyCallbacks.push({
            func: onYtReady,
            context: ctx,
        });
    }
    if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => {
            window.videoPlayerReadyCallbacks.forEach(cb => {
                cb.func(cb.context);
            });
            window.videoPlayerReadyCallbacks.length = 0;
        };
    }
    loadYoutubeIframeAPIScript();
};
class VideoPlayerPlus extends s {
    constructor() {
        super();
        this.usedInFullImageVideo = false;
        this.usedInInlineFullVideo = false;
        this.reduceAnimation = false;
        this.usedInInline = false;
        this.insideTextBlock = false;
        this.isVideoStarted = false;
        this.player = null;
        this.showAudioDescription = false;
        this.theaterMode = false;
        this.showTranscription = false;
        this.GET_IFRAME_PARAMS = () => ({
            YouTube: `?playsinline=1&modestbranding=1${this.usedInFullImageVideo ? '&autoplay=1&loop=1&mute=1&controls=0' : ''}`,
            Vimeo: `?background=${this.usedInFullImageVideo ? '1' : '0'}`,
            inInlineFull: '&autoplay=1',
        });
        this.videoStartedHandler = (e, id) => {
            if (this.isVideoStarted)
                return;
            this.isVideoStarted = true;
            trackVideoStarted(e, id);
        };
        this.onYouTubePlayerStateChange = (event) => {
            switch (event.data) {
                case window.YT.PlayerState.ENDED:
                    trackYoutubeVideoEnded(this.player.getVideoUrl(), this.player.getVideoData().title);
                    break;
                case window.YT.PlayerState.PLAYING:
                    if (!this.isVideoStarted) {
                        trackYoutubeVideoStarted(this.player.getVideoUrl(), this.player.getVideoData().title);
                        setInterval(() => this.player.trackPostionHanler(this.player.getVideoUrl(), this.player.getVideoData().title, this.player.getDuration(), this.player.getCurrentTime(), event.data === window.YT.PlayerState.PLAYING), 300);
                        this.isVideoStarted = true;
                    }
                    break;
            }
        };
        this.onYouTubePlayerReady = () => {
            this.player.trackPostionHanler = trackYoutubeVideoPosition();
            if (this.playWhenReady) {
                this.playWhenReady = false;
                this._ensurePlaying();
            }
        };
        this.playWhenReady = false;
        this.trackVideoPosition = trackVideoPosition();
        this.renderVideoBlock = () => {
            let iframeSrc = '';
            switch (this.type) {
                case VideoTypes.LOCAL:
                    return $ `<div class="video-player-wrapper">${renderLocalVideo(this)}</div>`;
                case VideoTypes.YOUTUBE:
                    iframeSrc = `https://www.youtube-nocookie.com/embed/${this.videoId + this.setIframeParams(VideoTypes.YOUTUBE)}`;
                    return this.usedInFullImageVideo
                        ? $ `<iframe title="youtube player" .src="${iframeSrc}" allowfullscreen allow="autoplay"></iframe>`
                        : $ `<div id="player_${this.videoId}"></div>`;
                case VideoTypes.VIMEO:
                    iframeSrc = `https://player.vimeo.com/video/${this.videoId + this.setIframeParams(VideoTypes.VIMEO)}`;
                    return $ `<iframe title="vimeo player" .src="${iframeSrc}" allowfullscreen allow="autoplay"></iframe>`;
                default:
                    return null;
            }
        };
        this._query = '(max-width: 767px)';
        this._isMobile = false;
        this._isAutoplay = !!(this.autoplay && !this._isMobile);
    }
    _handleMediaQuery(event) {
        this._isMobile = event.detail.value;
        this.requestUpdate();
    }
    _triggerLocalVideo() {
        var _a;
        if (this._isAutoplay &&
            this.type === VideoTypes.LOCAL &&
            this.usedInFullImageVideo) {
            const video = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('video');
            video.muted = !!this.muted;
            video === null || video === void 0 ? void 0 : video.play();
        }
    }
    // See video-player: re-issue playVideo until playback starts, to survive the
    // iframe reload caused by the lightbox re-parenting the player on open.
    _ensurePlaying(attempt = 0) {
        var _a, _b, _c, _d;
        const state = (_b = (_a = this.player) === null || _a === void 0 ? void 0 : _a.getPlayerState) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (state === 1 || state === 3)
            return; // playing / buffering
        (_d = (_c = this.player) === null || _c === void 0 ? void 0 : _c.playVideo) === null || _d === void 0 ? void 0 : _d.call(_c);
        if (attempt < 8) {
            setTimeout(() => this._ensurePlaying(attempt + 1), 250);
        }
    }
    initPlayer() {
        if (this.type !== VideoTypes.YOUTUBE)
            return;
        requestYoutubePlayerInit(this);
    }
    initAndPlay() {
        var _a;
        if (this.type !== VideoTypes.YOUTUBE)
            return false;
        if ((_a = this.player) === null || _a === void 0 ? void 0 : _a.playVideo) {
            this._ensurePlaying();
            return true;
        }
        this.mountCookieFreeIframe(true);
        if (hasYoutubeConsent()) {
            this.playWhenReady = true;
            this.initPlayer();
        }
        else {
            // See video-player: playback is cookie-free via the nocookie embed; the
            // API (whose bootstrap sets .youtube.com cookies) attaches only on
            // consent, and playWhenReady stays false so it never (re)starts playback.
            whenYoutubeConsent(() => this.initPlayer());
        }
        return true;
    }
    // See video-player: mount the nocookie iframe synchronously in the click so
    // the gesture carries into the iframe load and autoplay is permitted; the
    // IFrame API attaches to it (enablejsapi + origin) if consent allows.
    mountCookieFreeIframe(withAutoplay = true) {
        var _a;
        const id = `player_${this.videoId}`;
        const mount = ((_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.getElementById(id)) || document.getElementById(id);
        if (!mount || mount.tagName === 'IFRAME')
            return;
        const iframe = document.createElement('iframe');
        iframe.id = id;
        iframe.title = this.videoname || 'youtube player';
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        iframe.setAttribute('allowfullscreen', '');
        iframe.src = `https://www.youtube-nocookie.com/embed/${this.videoId}?rel=0&playsinline=1${withAutoplay ? '&autoplay=1' : ''}&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
        mount.replaceWith(iframe);
    }
    firstUpdated() {
        var _a;
        const parentVideoBlock = this.parentElement;
        if ((_a = parentVideoBlock === null || parentVideoBlock === void 0 ? void 0 : parentVideoBlock.parentElement) === null || _a === void 0 ? void 0 : _a.classList.contains('rich-text')) {
            this.insideTextBlock = true;
        }
        if (this.type === VideoTypes.YOUTUBE && this.autoinit) {
            whenYoutubeConsent(() => this.initPlayer());
        }
        if (this.type === VideoTypes.LOCAL) {
            initializePlyr(this);
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.player && typeof this.player.destroy === 'function') {
            this.player.destroy();
        }
    }
    updated(changedProperties) {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.reduceAnimation = !!(!mediaQuery || mediaQuery.matches);
        this._triggerLocalVideo();
        this._isAutoplay = !!(this.autoplay && !this.reduceAnimation);
        if (changedProperties.has('showAudioDescription')) {
            handleADStateChange(this.showAudioDescription, this);
        }
        if (changedProperties.has('showTranscription')) {
            handleTranscriptionStateChange(this.showTranscription, this);
        }
    }
    setIframeParams(videoType) {
        if (!this._isAutoplay)
            return '';
        let params = this.GET_IFRAME_PARAMS()[videoType];
        if (this.usedInInlineFullVideo) {
            params += this.GET_IFRAME_PARAMS().inInlineFull;
        }
        if (videoType === VideoTypes.YOUTUBE) {
            params += `&playlist=${this.videoId}`;
        }
        return params;
    }
    render() {
        return $ `<div class="video-container ${this.usedInFullImageVideo ? 'inFullImageVideo' : ''} ${this.usedInInlineFullVideo ? 'usedInInlineFullVideo' : ''} ${this.insideTextBlock ? 'insideTextBlock' : ''} ${this.usedInInline ? 'usedInInline' : ''} ${this.isLightbox ? 'lightbox-video' : ''}">${this.renderVideoBlock()} ${renderAudioDescription(this)} ${renderTranscription(this)}</div><lit-media-query .query="${this._query}" @changed="${this._handleMediaQuery}"></lit-media-query>`;
    }
}
VideoPlayerPlus.styles = css_248z;
__decorate([
    e()
], VideoPlayerPlus.prototype, "type", void 0);
__decorate([
    e()
], VideoPlayerPlus.prototype, "videoId", void 0);
__decorate([
    e()
], VideoPlayerPlus.prototype, "videoname", void 0);
__decorate([
    e({ type: String })
], VideoPlayerPlus.prototype, "closedCaptions", void 0);
__decorate([
    e({ type: String })
], VideoPlayerPlus.prototype, "closedCaptionsLang", void 0);
__decorate([
    e({ type: String })
], VideoPlayerPlus.prototype, "audioDescription", void 0);
__decorate([
    e({ type: String })
], VideoPlayerPlus.prototype, "audioDescriptionLang", void 0);
__decorate([
    e({ type: Boolean })
], VideoPlayerPlus.prototype, "autoinit", void 0);
__decorate([
    e({ type: Boolean })
], VideoPlayerPlus.prototype, "autoplay", void 0);
__decorate([
    e({ type: Boolean })
], VideoPlayerPlus.prototype, "muted", void 0);
__decorate([
    e({ type: Boolean })
], VideoPlayerPlus.prototype, "loop", void 0);
__decorate([
    e({ type: Boolean })
], VideoPlayerPlus.prototype, "usedInFullImageVideo", void 0);
__decorate([
    e({ type: Boolean })
], VideoPlayerPlus.prototype, "usedInInlineFullVideo", void 0);
__decorate([
    e({ type: Boolean })
], VideoPlayerPlus.prototype, "reduceAnimation", void 0);
__decorate([
    e({ type: Boolean })
], VideoPlayerPlus.prototype, "usedInInline", void 0);
__decorate([
    e({ type: Boolean })
], VideoPlayerPlus.prototype, "isLightbox", void 0);
__decorate([
    t()
], VideoPlayerPlus.prototype, "_isAutoplay", void 0);
__decorate([
    t()
], VideoPlayerPlus.prototype, "showAudioDescription", void 0);
__decorate([
    t()
], VideoPlayerPlus.prototype, "theaterMode", void 0);
__decorate([
    t()
], VideoPlayerPlus.prototype, "showTranscription", void 0);
customElements.define('video-player-plus', VideoPlayerPlus);

export { VideoPlayerPlus, loadYoutubeIframeAPIScript, onYtReady, requestYoutubePlayerInit };
